/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);

      // Replace True values on initial page load
      this.replaceActiveFilterLabelsOnLoad();
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      // Capture filter label data when a filter is selected
      this.captureFilterLabel(evt);

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Captures the filter label when a filter is selected
     * @param {object} evt - Event object.
     */
    captureFilterLabel(evt) {
      const target = evt.target;
      
      // Skip if it's not a checkbox or radio input
      if (target.type !== 'checkbox' && target.type !== 'radio') return;
      
      // Initialize selectedFilters object if it doesn't exist
      if (!this.selectedFilters) {
        this.selectedFilters = new Map();
      }
      
      // Find the label associated with this input
      let filterLabel = '';
      
      // Try to find label by 'for' attribute
      const labelElement = document.querySelector(`label[for="${target.id}"]`);
      if (labelElement) {
        filterLabel = labelElement.textContent?.trim();
      } else {
        // Try to find parent label
        const parentLabel = target.closest('label');
        if (parentLabel) {
          filterLabel = parentLabel.textContent?.trim();
        }
      }
      
      console.log('Captured filter:', {
        name: target.name,
        value: target.value,
        label: filterLabel,
        checked: target.checked
      });
      
      // Store or remove the filter label based on checked state
      if (target.checked) {
        this.selectedFilters.set(`${target.name}:${target.value}`, filterLabel);
      } else {
        this.selectedFilters.delete(`${target.name}:${target.value}`);
      }
      
      console.log('Current selected filters:', this.selectedFilters);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      const target = evt.target.closest('a.active-filter');
      if (!target) return;
      
      evt.preventDefault();
      
      // Get the filter key and name from data attributes
      const filterKey = target.getAttribute('data-filter-key');
      const filterName = target.getAttribute('data-filter-name');
      
      // If this is a "True" value filter, verify we're removing the correct one
      if (filterKey && filterName) {
        const currentUrl = new URL(window.location.href);
        const currentParams = new URLSearchParams(currentUrl.search);
        
        // Verify this filter is actually active
        if (currentParams.get(filterKey) === 'True') {
          const targetUrl = new URL(target.href);
          const targetParams = new URLSearchParams(targetUrl.search);
          
          // Verify this link would remove the correct filter
          if (!targetParams.has(filterKey) || targetParams.get(filterKey) !== 'True') {
            this.applyFilters(targetParams.toString(), evt);
            return;
          }
        }
      }
      
      // Fall back to default behavior for other types of filters
      this.applyFilters(new URL(target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = ''; 

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          let responseText = await response.text();
          
          // Replace True values in the response HTML before parsing
          responseText = this.replaceFilterTrueValues(responseText, searchParams);
          
          const tmpl = document.createElement('template');
          tmpl.innerHTML = responseText;

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the active filters section
          const newActiveFilters = tmpl.content.querySelector('.facets__active-filters');
          if (newActiveFilters && this.activeFilters) {
            this.activeFilters.innerHTML = newActiveFilters.innerHTML;
          }

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Replaces "True" values in the response HTML with actual filter names
     */
    replaceFilterTrueValues(html, searchParams) {
      // Parse the search params to find filter names
      const urlParams = new URLSearchParams(searchParams);
      
      // Create a map of filter parameters that have "True" values
      const trueFilters = new Map();
      
      // First pass: collect all True filters and their names
      for (const [key, value] of urlParams.entries()) {
        if (value.toLowerCase() === 'true' && key.includes('filter.p.m.custom.')) {
          const filterName = key.split('.').pop();
          if (filterName) {
            trueFilters.set(key, filterName);
          }
        }
      }
      
      // Create a DOM parser to work with the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find all active filter links
      const activeFilterLinks = doc.querySelectorAll('a.active-filter');
      
      activeFilterLinks.forEach(link => {
        const linkUrl = new URL(link.href, window.location.origin);
        const linkParams = new URLSearchParams(linkUrl.search);
        
        // Find which filter this link corresponds to by comparing parameters
        for (const [filterKey, filterName] of trueFilters) {
          const hasFilterInCurrent = urlParams.get(filterKey) === 'True';
          const hasFilterInLink = linkParams.get(filterKey) === 'True';
          
          // This link removes this specific filter
          if (hasFilterInCurrent && !hasFilterInLink) {
            // Store the filter key and name as data attributes
            link.setAttribute('data-filter-key', filterKey);
            link.setAttribute('data-filter-name', filterName);
            
            // Replace the text content while preserving other elements (like the close icon)
            const textNodes = Array.from(link.childNodes).filter(node => 
              node.nodeType === Node.TEXT_NODE && node.textContent.toLowerCase().includes('true')
            );
            
            textNodes.forEach(node => {
              node.textContent = node.textContent.replace(/true/gi, filterName);
            });
            
            // Break since we found the matching filter
            break;
          }
        }
      });
      
      // Convert back to string
      return doc.documentElement.outerHTML;
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }

    /**
     * Replaces "True" values in active filters on initial page load
     */
    replaceActiveFilterLabelsOnLoad() {
      if (!this.activeFilters) return;
      
      // Get current URL parameters
      const currentUrl = new URL(window.location.href);
      const currentParams = currentUrl.searchParams;
      
      // Create a map of filter parameters that have "True" values
      const trueFilters = new Map();
      
      for (const [key, value] of currentParams.entries()) {
        if (value.toLowerCase() === 'true' && key.includes('filter.p.m.custom.')) {
          const filterName = key.split('.').pop();
          if (filterName) {
            trueFilters.set(key, filterName);
          }
        }
      }
      
      // Find and update active filter links
      const activeFilterLinks = this.activeFilters.querySelectorAll('a.active-filter');
      
      activeFilterLinks.forEach((link) => {
        const linkText = link.textContent?.trim();
        
        if (linkText && linkText.toLowerCase().includes('true')) {
          // Parse the link's URL to see which filters would remain after clicking
          const linkUrl = new URL(link.href, window.location.origin);
          const linkParams = linkUrl.searchParams;
          
          // Find which filter is removed by this link
          for (const [filterKey, filterName] of trueFilters) {
            const currentHasFilter = currentParams.get(filterKey) === 'True';
            const linkHasFilter = linkParams.get(filterKey) === 'True';
            
            // If current page has this filter but the link doesn't, this link removes this filter
            if (currentHasFilter && !linkHasFilter) {
              // Get all child nodes
              const childNodes = Array.from(link.childNodes);
              
              // Find and replace text nodes that contain "True"
              childNodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE && node.textContent.toLowerCase().includes('true')) {
                  const newText = node.textContent.replace(/true/gi, filterName);
                  node.textContent = newText;
                }
              });
              
              // Also store the filter name in a data attribute
              link.setAttribute('data-filter-label', filterName);
              
              console.log(`Initial load: Replaced "True" with "${filterName}" while preserving SVG`);
              break;
            }
          }
        }
      });
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
